// Common passwords that should be rejected (Django's default list)
const COMMON_PASSWORDS = [
  'password',
  '123456',
  '123456789',
  'qwerty',
  'abc123',
  'password1',
  'admin',
  'letmein',
  'welcome',
  'monkey',
  'football',
  'iloveyou',
]

export interface UserInfo {
  firstName?: string
  lastName?: string
  email?: string
}

/**
 * Check if password is too similar to user information
 * Based on Django's CommonPasswordValidator
 */
function isSimilarToUserInfo(password: string, userInfo: UserInfo): boolean {
  const lowerPassword = password.toLowerCase()
  const fieldsToCheck = [
    userInfo.firstName?.toLowerCase(),
    userInfo.lastName?.toLowerCase(),
    userInfo.email?.toLowerCase().split('@')[0],
  ].filter(Boolean)

  return fieldsToCheck.some((field) => {
    if (!field || field.length < 3) return false
    // Check if password contains significant portion of user info
    return lowerPassword.includes(field) || field.includes(lowerPassword)
  })
}

/**
 * Django allauth password validation (with custom requirements)
 */
export function validatePassword(
  password: string,
  userInfo?: UserInfo,
): { isValid: boolean; message?: string } {
  // 1. MinimumLengthValidator - minimum 8 characters
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'Password must be at least 8 characters long',
    }
  }

  // 2. CommonPasswordValidator - not a common password
  if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
    return {
      isValid: false,
      message: 'This password is too common',
    }
  }

  // 3. NumericPasswordValidator - not entirely numeric
  if (/^\d+$/.test(password)) {
    return {
      isValid: false,
      message: 'This password is entirely numeric',
    }
  }

  // 4. AttributeSimilarityValidator - not too similar to user info
  if (userInfo && isSimilarToUserInfo(password, userInfo)) {
    return {
      isValid: false,
      message: 'The password is too similar to your personal information',
    }
  }

  // 5. Custom requirement - must contain at least one special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return {
      isValid: false,
      message:
        'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;\':"\\,./<>?)',
    }
  }

  return { isValid: true }
}
